import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  clearProductCart,
  updateCartItemQuantity,
  setCartItemQuantity,
  removeCustomDesign,
  updateCustomDesignQuantity,
  setCartOpen,
} from "../actions/cartActions";
import { ShoppingCart, PawPrint } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";

const GlobalCart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = useSelector((state) => state.cart.isOpen);
  const cartState = useSelector((state) => state.cart);

  const setIsOpen = (open) => dispatch(setCartOpen(open));

  const PRODUCT_IMAGES = {
    "qr-collar": {
      orange: "/img/1-1.webp",
      cream: "/img/1-2.webp",
      black: "/img/1-3.webp",
      pink: "/img/1-4.webp",
    },
    "gps-tracker": {
      orange: "/img/circle-yellow-1.webp",
      cream: "/img/circle-blue-1.webp",
      black: "/img/circle-gray-1.webp",
      pink: "/img/circle-pink-1.webp",
    },
  };

  // --- სქროლვის შეზღუდვის ლოგიკა ---
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const calculateProductTotal = (cart) => {
    const standardTotal = Object.values(cart.colorQuantities).reduce(
      (sum, qty) => sum + qty,
      0,
    );
    const customTotal = cart.customDesigns
      .filter((d) => d.image)
      .reduce((sum, d) => sum + (d.quantity || 0), 0);
    return standardTotal + customTotal;
  };

  const qrCollarTotal = calculateProductTotal(cartState.qrCollar);
  const gpsTrackerTotal = calculateProductTotal(cartState.gpsTracker);
  const totalItems = qrCollarTotal + gpsTrackerTotal;

  // 1. კალათის შენარჩუნება და სინქრონიზაცია
  useEffect(() => {
    if (totalItems === 0) {
      localStorage.removeItem("fethub_cart");
      localStorage.removeItem("cart_expiry");
      return;
    }

    const expiry =
      localStorage.getItem("cart_expiry") ||
      (new Date().getTime() + 25 * 60 * 1000).toString();

    localStorage.setItem(
      "fethub_cart",
      JSON.stringify({
        data: cartState,
        expiry: parseInt(expiry),
      }),
    );

    if (!localStorage.getItem("cart_expiry")) {
      localStorage.setItem("cart_expiry", expiry);
    }
  }, [cartState, totalItems]);

  // 2. ტაიმერის შემოწმება
  useEffect(() => {
    const checkExpiry = () => {
      const cartExpiry = localStorage.getItem("cart_expiry");
      const now = new Date().getTime();

      if (cartExpiry && now > parseInt(cartExpiry)) {
        dispatch(clearProductCart("qr-collar"));
        dispatch(clearProductCart("gps-tracker"));
        localStorage.removeItem("cart_expiry");
        localStorage.removeItem("fethub_cart");
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const qrUnitPrice = 29;
  const gpsUnitPrice = 205;
  const shippingPrice = 0; // 6
  const qrSubtotal = qrUnitPrice * qrCollarTotal;
  const gpsSubtotal = gpsUnitPrice * gpsTrackerTotal;
  const subtotalAll = qrSubtotal + gpsSubtotal;
  const totalPrice = totalItems > 0 ? subtotalAll + shippingPrice : 0;

  const handleClearQRCart = () => dispatch(clearProductCart("qr-collar"));
  const handleClearGPSCart = () => dispatch(clearProductCart("gps-tracker"));

  const renderCartItem = (productType, color, quantity) => {
    if (quantity === 0) return null;
    const unitPrice =
      productType === "gps-tracker" ? gpsUnitPrice : qrUnitPrice;
    const productLabel =
      productType === "gps-tracker" ? t("gps tracker") : t("qr tag");
    const itemImage = PRODUCT_IMAGES[productType]?.[color];

    return (
      <div key={`${productType}-${color}`} className="cart-item">
        <div className="cart-item-image">
          {itemImage ? (
            <img src={itemImage} alt={`${color}`} />
          ) : (
            <div
              className="image-placeholder"
              style={{ backgroundColor: color }}
            ></div>
          )}
        </div>
        <div className="cart-item-info">
          <span className="cart-item-name">
            {productLabel} - {t(color)}
          </span>
          <div className="cart-item-quantity">
            <button
              className="qty-btn"
              onClick={() =>
                dispatch(updateCartItemQuantity(productType, color, -1))
              }
            >
              -
            </button>
            <input type="number" value={quantity} readOnly />
            <button
              className="qty-btn"
              onClick={() =>
                dispatch(updateCartItemQuantity(productType, color, 1))
              }
            >
              +
            </button>
          </div>
        </div>
        <span className="cart-item-price">
          {unitPrice * quantity} {t("gel")}
        </span>
      </div>
    );
  };

  const renderCustomDesigns = (productType, customDesigns) => {
    const validDesigns = customDesigns.filter((d) => d.image && d.quantity > 0);
    if (validDesigns.length === 0) return null;
    const unitPrice =
      productType === "gps-tracker" ? gpsUnitPrice : qrUnitPrice;
    const productLabel =
      productType === "gps-tracker" ? t("gps tracker") : t("qr tag");

    return validDesigns.map((design, index) => (
      <div key={`${productType}-custom-${design.id}`} className="cart-item">
        <div className="cart-item-image">
          <img src="/img/1-5.webp" alt="Custom" />
        </div>
        <div className="cart-item-info">
          <span className="cart-item-name">
            {productLabel} - {t("custom design")} #{index + 1}
          </span>
          <div className="cart-item-quantity">
            <button
              className="qty-btn"
              onClick={() =>
                dispatch(
                  updateCustomDesignQuantity(
                    productType,
                    design.id,
                    design.quantity - 1,
                  ),
                )
              }
            >
              -
            </button>
            <input type="number" value={design.quantity} readOnly />
            <button
              className="qty-btn"
              onClick={() =>
                dispatch(
                  updateCustomDesignQuantity(
                    productType,
                    design.id,
                    design.quantity + 1,
                  ),
                )
              }
            >
              +
            </button>
          </div>
        </div>
        <div className="cart-item-actions">
          <span className="cart-item-price">
            {unitPrice * design.quantity} {t("gel")}
          </span>
          <button
            className="remove-btn"
            onClick={() => dispatch(removeCustomDesign(productType, design.id))}
          >
            &times;
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      <button className="global-cart-button" onClick={() => setIsOpen(!isOpen)}>
        {/* <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="9" cy="21" r="1"></circle>
          <circle cx="20" cy="21" r="1"></circle>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg> */}
        <FontAwesomeIcon icon={faCartShopping} />
        {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
      </button>

      {isOpen && (
        <>
          <div className="cart-overlay" onClick={() => setIsOpen(false)}></div>
          <div className="cart-sidebar">
            <div className="cart-header">
              <h3>{t("cart")}</h3>
              <button className="close-cart" onClick={() => setIsOpen(false)}>
                &times;
              </button>
            </div>

            <div
              className="cart-content"
              style={{ flex: 1, overflowY: "auto" }}
            >
              {totalItems === 0 ? (
                <div className="empty-cart">
                  <div className="empty-cart-icon-and-quantity">
                    {/* <ShoppingCart /> */}
                    <FontAwesomeIcon icon={faCartShopping} />
                    <span className="empty-cart-quantity">0</span>
                  </div>

                  <p>{t("cart_is_empty")}</p>
                  <button
                    onClick={() => {
                      navigate("/order/qrcollar");
                      setIsOpen(false);
                    }}
                  >
                    {t("buy now")}
                  </button>
                </div>
              ) : (
                <>
                  {qrCollarTotal > 0 && (
                    <div className="cart-section">
                      <div className="cart-section-header">
                        <h4>{t("qr tag")}</h4>
                        <button
                          className="clear-section"
                          onClick={handleClearQRCart}
                        >
                          {t("clear")}
                        </button>
                      </div>
                      {Object.entries(cartState.qrCollar.colorQuantities).map(
                        ([color, qty]) =>
                          renderCartItem("qr-collar", color, qty),
                      )}
                      {renderCustomDesigns(
                        "qr-collar",
                        cartState.qrCollar.customDesigns,
                      )}
                    </div>
                  )}
                  {gpsTrackerTotal > 0 && (
                    <div className="cart-section">
                      <div className="cart-section-header">
                        <h4>{t("gps tracker")}</h4>
                        <button
                          className="clear-section"
                          onClick={handleClearGPSCart}
                        >
                          {t("clear")}
                        </button>
                      </div>
                      {Object.entries(cartState.gpsTracker.colorQuantities).map(
                        ([color, qty]) =>
                          renderCartItem("gps-tracker", color, qty),
                      )}
                      {renderCustomDesigns(
                        "gps-tracker",
                        cartState.gpsTracker.customDesigns,
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {totalItems > 0 && (
              <div className="cart-footer">
                <div className="cart-shipping">
                  <span>{t("shipping")}:</span>
                  <span>
                    {shippingPrice} {t("gel")}
                  </span>
                </div>
                <div className="cart-total">
                  <span>{t("total")}:</span>
                  <span className="total-price">
                    {totalPrice} {t("gel")}
                  </span>
                </div>
                <button
                  className="checkout-link"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/checkout");
                  }}
                >
                  {t("place order")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default GlobalCart;
